import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Observable, defer, map, of, switchMap, tap, timer } from 'rxjs';

/**
 * Place search (Nominatim), routing (OSRM) and local query parsing for the GIS map.
 *
 * Requests go straight from the browser to the public OpenStreetMap services, so they use an
 * HttpClient built on HttpBackend: the app interceptors would otherwise attach the user's JWT
 * and custom headers to third-party requests.
 *
 * Nominatim usage policy: at most one request per second, no search-as-you-type, attribution shown.
 */
export const NOMINATIM_URL = 'https://nominatim.openstreetmap.org';
export const OSRM_URLS: Record<GeoRouteProfile, string> = {
  car: 'https://routing.openstreetmap.de/routed-car',
  bike: 'https://routing.openstreetmap.de/routed-bike',
  foot: 'https://routing.openstreetmap.de/routed-foot',
};

const NOMINATIM_MIN_INTERVAL_MS = 1000;
const CACHE_SIZE = 50;
const RECENT_LIMIT = 10;
const SAVED_LIMIT = 100;

export type GeoRouteProfile = 'car' | 'bike' | 'foot';
export type GeoPlaceKind = 'coords' | 'chainage' | 'feature' | 'place' | 'saved' | 'recent' | 'picked';

export interface GeoPlace {
  id: string;
  kind: GeoPlaceKind;
  label: string;
  sublabel?: string;
  lon: number;
  lat: number;
  /** [minLon, minLat, maxLon, maxLat] in EPSG:4326. */
  bbox?: [number, number, number, number];
  /** Opaque handle for project-feature results (owned by the host component). */
  ref?: unknown;
}

export interface GeoRouteStep {
  instruction: string;
  distance_m: number;
  duration_s: number;
  lon: number;
  lat: number;
}

export interface GeoRoute {
  geometry: GeoJSON.LineString;
  distance_m: number;
  duration_s: number;
  steps: GeoRouteStep[];
}

export interface ParsedChainage {
  routeCode: string | null;
  measureKm: number;
}

const DMS_COMPONENT =
  String.raw`([NSEW])?\s*([-+]?\d+(?:\.\d+)?)(?:\s*[°º]\s*(?:(\d+(?:\.\d+)?)\s*['′]\s*)?(?:(\d+(?:\.\d+)?)\s*(?:["″]|'')\s*)?)?\s*([NSEW])?`;
const COORD_PAIR_RE = new RegExp(String.raw`^\s*${DMS_COMPONENT}\s*[,;\s]\s*${DMS_COMPONENT}\s*$`, 'i');
const CHAINAGE_PLUS_RE = /^(?:(.+?)\s+)?(?:km|ch|chainage)?\.?\s*:?\s*(\d+)\s*\+\s*(\d{1,3}(?:\.\d+)?)\s*$/i;
const CHAINAGE_KM_RE = /^(?:(.+?)\s+)?(?:km|ch|chainage)\.?\s*:?\s*(\d+(?:\.\d+)?)\s*(?:km)?\s*$/i;

@Injectable({ providedIn: 'root' })
export class GeoSearchService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly cache = new Map<string, GeoPlace[]>();
  private nextNominatimSlot = 0;

  // ---------------------------------------------------------------- parsing

  /**
   * `28.61, 77.20`, `77.20 28.61`, `N28.61 E77.20`, `28°36'50"N 77°12'32"E`. Empty when not a coordinate.
   * Without hemisphere letters a pair that is valid both ways yields lat,lon first and lon,lat second.
   */
  parseCoordinates(query: string): GeoPlace[] {
    const m = COORD_PAIR_RE.exec(String(query || '').replace(/[\u2019]/g, "'").replace(/[\u201d]/g, '"'));
    if (!m) return [];
    const a = this.componentValue(m[1], m[2], m[3], m[4], m[5]);
    const b = this.componentValue(m[6], m[7], m[8], m[9], m[10]);
    if (!a || !b) return [];

    const pairs: Array<[number, number]> = [];
    if (a.axis === 'lon' || b.axis === 'lat') {
      pairs.push([b.value, a.value]);
    } else if (a.axis === 'lat' || b.axis === 'lon') {
      pairs.push([a.value, b.value]);
    } else {
      pairs.push([a.value, b.value], [b.value, a.value]);
    }
    return pairs
      .filter(([lat, lon]) => Math.abs(lat) <= 90 && Math.abs(lon) <= 180)
      .filter((p, i, all) => i === 0 || p[0] !== all[0][0])
      .map(([lat, lon], i) => {
        const label = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
        return {
          id: `coords:${label}`,
          kind: 'coords' as const,
          label,
          sublabel: i === 0 ? 'Latitude, longitude' : 'Read as longitude, latitude',
          lon,
          lat,
        };
      });
  }

  /** `km 12.5`, `12+500`, `CH 12.500`, `NH48 12+500`; null when not a chainage. */
  parseChainage(query: string): ParsedChainage | null {
    const q = String(query || '').trim();
    let m = CHAINAGE_PLUS_RE.exec(q);
    if (m) {
      return { routeCode: m[1]?.trim() || null, measureKm: parseInt(m[2], 10) + parseFloat(m[3]) / 1000 };
    }
    m = CHAINAGE_KM_RE.exec(q);
    if (m) {
      return { routeCode: m[1]?.trim() || null, measureKm: parseFloat(m[2]) };
    }
    return null;
  }

  private componentValue(
    hemiBefore: string | undefined,
    deg: string,
    min: string | undefined,
    sec: string | undefined,
    hemiAfter: string | undefined
  ): { value: number; axis: 'lat' | 'lon' | null } | null {
    if (hemiBefore && hemiAfter) return null;
    const hemi = (hemiBefore || hemiAfter || '').toUpperCase();
    const d = parseFloat(deg);
    const mm = min ? parseFloat(min) : 0;
    const ss = sec ? parseFloat(sec) : 0;
    if (!Number.isFinite(d) || mm >= 60 || ss >= 60) return null;
    let value = Math.abs(d) + mm / 60 + ss / 3600;
    if (d < 0 || deg.startsWith('-') || hemi === 'S' || hemi === 'W') value = -value;
    const axis = hemi === 'N' || hemi === 'S' ? 'lat' : hemi === 'E' || hemi === 'W' ? 'lon' : null;
    return { value, axis };
  }

  // ---------------------------------------------------------------- Nominatim

  /** Free-text place search, biased (not bounded) to the given [minLon, minLat, maxLon, maxLat] viewbox. */
  search(query: string, viewbox?: [number, number, number, number]): Observable<GeoPlace[]> {
    const q = String(query || '').trim();
    if (!q) return of([]);
    const vb = viewbox ? viewbox.map((n) => n.toFixed(3)).join(',') : '';
    const key = `${q.toLowerCase()}|${vb}`;
    const hit = this.cache.get(key);
    if (hit) {
      this.cache.delete(key);
      this.cache.set(key, hit);
      return of(hit);
    }

    let params = new HttpParams()
      .set('format', 'jsonv2')
      .set('q', q)
      .set('limit', '8')
      .set('addressdetails', '1')
      .set('accept-language', this.language());
    if (vb) params = params.set('viewbox', vb).set('bounded', '0');

    return this.throttled(() => this.http.get<NominatimResult[]>(`${NOMINATIM_URL}/search`, { params })).pipe(
      map((rows) => (rows || []).map((r) => this.toPlace(r))),
      tap((places) => {
        this.cache.set(key, places);
        if (this.cache.size > CACHE_SIZE) this.cache.delete(this.cache.keys().next().value as string);
      })
    );
  }

  /** Human-readable label for a point; falls back to the coordinates when nothing is found. */
  reverse(lon: number, lat: number): Observable<GeoPlace> {
    const fallback = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
    const params = new HttpParams()
      .set('format', 'jsonv2')
      .set('lat', String(lat))
      .set('lon', String(lon))
      .set('zoom', '18')
      .set('accept-language', this.language());
    return this.throttled(() => this.http.get<NominatimResult & { error?: string }>(`${NOMINATIM_URL}/reverse`, { params })).pipe(
      map((r) => {
        if (!r || r.error || !r.display_name) {
          return { id: `picked:${fallback}`, kind: 'picked' as const, label: fallback, lon, lat };
        }
        const place = this.toPlace(r);
        return { ...place, kind: 'picked' as const, lon, lat, bbox: undefined };
      })
    );
  }

  private toPlace(r: NominatimResult): GeoPlace {
    const parts = String(r.display_name || '').split(',').map((s) => s.trim()).filter(Boolean);
    const label = r.name || parts[0] || 'Unnamed place';
    const rest = parts[0] === label ? parts.slice(1) : parts;
    const bb = r.boundingbox?.map(Number);
    const bbox: GeoPlace['bbox'] =
      bb && bb.length === 4 && bb.every(Number.isFinite) ? [bb[2], bb[0], bb[3], bb[1]] : undefined;
    const type = r.type && r.type !== 'yes' ? r.type.replace(/_/g, ' ') : r.category || '';
    return {
      id: `osm:${r.osm_type || ''}${r.osm_id ?? r.place_id}`,
      kind: 'place',
      label,
      sublabel: [type ? type.charAt(0).toUpperCase() + type.slice(1) : '', rest.join(', ')].filter(Boolean).join(' · '),
      lon: parseFloat(r.lon),
      lat: parseFloat(r.lat),
      bbox,
    };
  }

  private throttled<T>(request: () => Observable<T>): Observable<T> {
    return defer(() => {
      const now = Date.now();
      const at = Math.max(now, this.nextNominatimSlot);
      this.nextNominatimSlot = at + NOMINATIM_MIN_INTERVAL_MS;
      return timer(at - now).pipe(switchMap(request));
    });
  }

  private language(): string {
    return (typeof navigator !== 'undefined' && navigator.language) || 'en';
  }

  // ---------------------------------------------------------------- OSRM

  route(profile: GeoRouteProfile, from: { lon: number; lat: number }, to: { lon: number; lat: number }): Observable<GeoRoute> {
    const coords = `${from.lon.toFixed(6)},${from.lat.toFixed(6)};${to.lon.toFixed(6)},${to.lat.toFixed(6)}`;
    const params = new HttpParams().set('overview', 'full').set('geometries', 'geojson').set('steps', 'true');
    return this.http.get<OsrmResponse>(`${OSRM_URLS[profile]}/route/v1/driving/${coords}`, { params }).pipe(
      map((res) => {
        const r = res?.routes?.[0];
        if (res?.code !== 'Ok' || !r) {
          const err = new Error(res?.message || 'No route found between these points');
          (err as Error & { code?: string }).code = res?.code || 'NoRoute';
          throw err;
        }
        const steps = (r.legs || []).flatMap((leg) => leg.steps || []).map((s) => ({
          instruction: this.instruction(s),
          distance_m: s.distance,
          duration_s: s.duration,
          lon: s.maneuver.location[0],
          lat: s.maneuver.location[1],
        }));
        return { geometry: r.geometry, distance_m: r.distance, duration_s: r.duration, steps };
      })
    );
  }

  private instruction(step: OsrmStep): string {
    const { type, modifier, exit } = step.maneuver;
    const road = step.name || step.ref || '';
    const onto = road ? ` onto ${road}` : '';
    const dir = modifier ? modifier.replace('uturn', 'U-turn') : '';
    switch (type) {
      case 'depart':
        return road ? `Head out on ${road}` : 'Start';
      case 'arrive':
        return modifier && modifier !== 'straight' ? `Arrive at the destination on the ${modifier.replace('slight ', '')}` : 'Arrive at the destination';
      case 'turn':
        if (modifier === 'uturn') return `Make a U-turn${onto}`;
        if (modifier === 'straight') return `Continue straight${onto}`;
        return `Turn ${dir}${onto}`;
      case 'new name':
      case 'continue':
        if (modifier === 'uturn') return `Make a U-turn${onto}`;
        return modifier && modifier !== 'straight' ? `Keep ${dir}${onto}` : `Continue${onto}`;
      case 'merge':
        return `Merge${dir ? ' ' + dir : ''}${onto}`;
      case 'fork':
        return `Keep ${dir || 'straight'} at the fork${onto}`;
      case 'on ramp':
        return `Take the ramp${dir ? ' on the ' + dir : ''}${onto}`;
      case 'off ramp':
        return `Take the exit${dir ? ' on the ' + dir : ''}${onto}`;
      case 'end of road':
        return `At the end of the road, turn ${dir || 'ahead'}${onto}`;
      case 'roundabout':
      case 'rotary':
        return exit ? `At the roundabout, take exit ${exit}${onto}` : `Enter the roundabout${onto}`;
      case 'roundabout turn':
        return `At the roundabout, turn ${dir}${onto}`;
      case 'exit roundabout':
      case 'exit rotary':
        return `Exit the roundabout${onto}`;
      default:
        return road ? `Continue on ${road}` : 'Continue';
    }
  }

  // ---------------------------------------------------------------- saved / recent places

  loadPlaces(list: 'saved' | 'recent'): GeoPlace[] {
    try {
      const raw = localStorage.getItem(this.storageKey(list));
      const rows = raw ? JSON.parse(raw) : [];
      return Array.isArray(rows)
        ? rows.filter((p) => p && Number.isFinite(p.lon) && Number.isFinite(p.lat) && typeof p.label === 'string')
        : [];
    } catch {
      return [];
    }
  }

  storePlaces(list: 'saved' | 'recent', places: GeoPlace[]): void {
    const limit = list === 'recent' ? RECENT_LIMIT : SAVED_LIMIT;
    const rows = places.slice(0, limit).map(({ ref: _ref, ...p }) => p);
    try {
      localStorage.setItem(this.storageKey(list), JSON.stringify(rows));
    } catch {
      // Storage full or disabled: places just are not remembered.
    }
  }

  private storageKey(list: 'saved' | 'recent'): string {
    let userId = 'anon';
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      userId = String(u?.id ?? u?.user?.id ?? u?.userDetails?.id ?? u?.data?.id ?? 'anon');
    } catch {
      // keep anon
    }
    return `gis.places.${list}.v1.${userId}`;
  }
}

interface NominatimResult {
  place_id: number;
  osm_type?: string;
  osm_id?: number;
  lat: string;
  lon: string;
  name?: string;
  display_name: string;
  category?: string;
  type?: string;
  boundingbox?: string[];
}

interface OsrmStep {
  distance: number;
  duration: number;
  name?: string;
  ref?: string;
  maneuver: { type: string; modifier?: string; exit?: number; location: [number, number] };
}

interface OsrmResponse {
  code: string;
  message?: string;
  routes?: Array<{
    geometry: GeoJSON.LineString;
    distance: number;
    duration: number;
    legs?: Array<{ steps?: OsrmStep[] }>;
  }>;
}
